import React, { useState, useEffect, useRef } from 'react';
import { WordRow } from './components/WordRow';
import { LetterListInput } from './components/LetterListInput';
import { ResultsList } from './components/ResultsList';
import { WordleApiService } from './services/wordleApi';
import { WordleApiParams, WordleApiResponse } from './types';
import { WORDLE_CONFIG } from './config';
import { useApiConfig } from './hooks/useApiConfig';

const DEBOUNCE_MS = 300;

const App: React.FC = () => {
  const { apiUrl, loading: configLoading, error: configError } = useApiConfig();

  const [knownLetters, setKnownLetters] = useState<string[]>(new Array(WORDLE_CONFIG.WORD_LENGTH).fill(''));
  const [withLetters, setWithLetters] = useState<string>('');
  const [withoutLetters, setWithoutLetters] = useState<string>('');
  const [knownNot1, setKnownNot1] = useState<string[]>(new Array(WORDLE_CONFIG.WORD_LENGTH).fill(''));
  const [knownNot2, setKnownNot2] = useState<string[]>(new Array(WORDLE_CONFIG.WORD_LENGTH).fill(''));
  const [knownNot3, setKnownNot3] = useState<string[]>(new Array(WORDLE_CONFIG.WORD_LENGTH).fill(''));

  const [results, setResults] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const abortRef = useRef<AbortController>();

  useEffect(() => {
    if (!apiUrl || configLoading) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();

      const params: WordleApiParams = {};
      if (withLetters.length > 0) params.with = withLetters;
      if (withoutLetters.length > 0) params.without = withoutLetters;

      const knownString = knownLetters.map(l => l || '.').join('');
      if (knownString !== '.....') params.known = knownString;

      const knownNotParams: string[] = [];
      [knownNot1, knownNot2, knownNot3].forEach(row => {
        const s = row.map(l => l || '.').join('');
        if (s !== '.....') knownNotParams.push(s);
      });
      if (knownNotParams.length > 0) params.knownnot = knownNotParams;

      if (!params.with && !params.without && !params.known && !params.knownnot) {
        setResults([]);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const apiService = new WordleApiService(apiUrl);
        const response: WordleApiResponse = await apiService.getSolutions(params);
        setResults(response.words || []);
      } catch (err) {
        if (err instanceof Error && err.message.includes('abort')) return;
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [apiUrl, configLoading, knownLetters, withLetters, withoutLetters, knownNot1, knownNot2, knownNot3]);

  const handleClear = () => {
    setKnownLetters(new Array(WORDLE_CONFIG.WORD_LENGTH).fill(''));
    setWithLetters('');
    setWithoutLetters('');
    setKnownNot1(new Array(WORDLE_CONFIG.WORD_LENGTH).fill(''));
    setKnownNot2(new Array(WORDLE_CONFIG.WORD_LENGTH).fill(''));
    setKnownNot3(new Array(WORDLE_CONFIG.WORD_LENGTH).fill(''));
    setResults([]);
    setError(null);
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#121213',
      color: '#d7dadc',
      fontFamily: "'Helvetica Neue', Arial, sans-serif",
    }}>
      {/* Header */}
      <header style={{
        borderBottom: '1px solid #3a3a3c',
        padding: '12px 0',
        textAlign: 'center',
      }}>
        <h1 style={{
          margin: 0,
          fontSize: '28px',
          fontWeight: 700,
          letterSpacing: '2px',
          textTransform: 'uppercase',
          color: '#fff',
        }}>
          Wordle Helper
        </h1>
      </header>

      <main style={{
        maxWidth: '520px',
        margin: '0 auto',
        padding: '24px 16px',
      }}>
        {/* Green letters - correct positions */}
        <section style={{ marginBottom: '24px', textAlign: 'center' }}>
          <label style={{
            display: 'block',
            fontSize: '12px',
            fontWeight: 600,
            color: '#818384',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '8px',
          }}>
            Correct positions
          </label>
          <WordRow letters={knownLetters} onChange={setKnownLetters} color="#538d4e" />
        </section>

        {/* Letter inputs */}
        <section style={{ marginBottom: '24px' }}>
          <LetterListInput
            value={withLetters}
            onChange={setWithLetters}
            label="In the word (wrong position)"
            placeholder="e.g. ATE"
          />
          <LetterListInput
            value={withoutLetters}
            onChange={setWithoutLetters}
            label="Not in the word"
            placeholder="e.g. XYZQW"
          />
        </section>

        {/* Yellow letters - wrong positions */}
        <section style={{ marginBottom: '24px', textAlign: 'center' }}>
          <label style={{
            display: 'block',
            fontSize: '12px',
            fontWeight: 600,
            color: '#818384',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '4px',
          }}>
            Not in these positions
          </label>
          <p style={{ fontSize: '12px', color: '#565758', margin: '0 0 8px 0' }}>
            Letters you know are in the word but not in these spots
          </p>
          <WordRow letters={knownNot1} onChange={setKnownNot1} label="Row 1" color="#b59f3b" />
          <WordRow letters={knownNot2} onChange={setKnownNot2} label="Row 2" color="#b59f3b" />
          <WordRow letters={knownNot3} onChange={setKnownNot3} label="Row 3" color="#b59f3b" />
        </section>

        {/* Clear button */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <button
            onClick={handleClear}
            disabled={configLoading}
            style={{
              backgroundColor: 'transparent',
              color: '#818384',
              padding: '8px 24px',
              fontSize: '13px',
              fontWeight: 600,
              border: '1px solid #3a3a3c',
              borderRadius: '20px',
              cursor: configLoading ? 'not-allowed' : 'pointer',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#818384';
              e.currentTarget.style.color = '#d7dadc';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#3a3a3c';
              e.currentTarget.style.color = '#818384';
            }}
          >
            Clear All
          </button>
        </div>

        {/* Divider */}
        <div style={{ borderTop: '1px solid #3a3a3c', marginBottom: '24px' }} />

        {/* Results */}
        <ResultsList words={results} loading={loading} error={error} />

        {configError && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: 'rgba(220, 53, 69, 0.1)',
            border: '1px solid rgba(220, 53, 69, 0.3)',
            borderRadius: '8px',
            color: '#ff6b6b',
            fontSize: '13px',
            textAlign: 'center',
          }}>
            {configError}
          </div>
        )}
      </main>
    </div>
  );
};

export default App;
